import React, { useState, useEffect, useCallback } from "react";
import "../page/supplierList.css";

import SupplierHeader from "../component/supplierHeader";
import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import SupplierTable from "../component/supplierTable";
import PaginationFooter from "../../../components/footer/paginationFooter";
import ModalAddSupplier from "../component/modalAddSupplier";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";

const SupplierList = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isOpenModalAddSupplier, setIsOpenModalAddSupplier] = useState(false);
  const [selectedSupplierData, setSelectedSupplierData] = useState({
    supplier_id: "",
    supplier_name: "",
    supplier_address: "",
    supplier_city: "",
    supplier_phone_number: "",
    supplier_tax_status: "",
    supplier_transaction_type: "",
    supplier_NPWP: "",
  });
  const [supplierData, setSupplierData] = useState(null);
  const [isOpenModalDeleteSupplier, setIsOpenModalDeleteSupplier] =
    useState(false);
  const [idSupplierToDelete, setIdSupplierToDelete] = useState("");
  const [searchSupplierKeyword, setSearchSupplierKeyword] = useState("");

  // =====================
  // Function Definition
  // =====================

  const addNewSupplier = () => {
    resetSelectedSupplierDataValue();
    setIsOpenModalAddSupplier(true);
  };

  const onEditSupplier = (supplierData) => {
    setSelectedSupplierData(supplierData);
    setIsOpenModalAddSupplier(true);
  };

  const onDeleteSupplier = (supplierData) => {
    setIsOpenModalDeleteSupplier(true);
    setIdSupplierToDelete(supplierData.supplier_id);
  };

  const onDeleteSupplierConfirmed = () => {
    setIsOpenModalDeleteSupplier(false);
    deleteSupplier();
  };

  const onCloseModalDeleteSupplier = () => {
    setIdSupplierToDelete("");
    setIsOpenModalDeleteSupplier(false);
  };

  const onCloseModal = () => {
    resetSelectedSupplierDataValue();
    setIsOpenModalAddSupplier(false);
  };

  const onSubmitNewSupplier = (supplierData, isEditData) => {
    setIsOpenModalAddSupplier(false);
    if (isEditData) {
      editSupplier(supplierData);
    } else {
      createNewSupplier(supplierData);
    }
  };

  const resetSelectedSupplierDataValue = () => {
    setSelectedSupplierData({
      supplier_id: "",
      supplier_name: "",
      supplier_address: "",
      supplier_city: "",
      supplier_phone_number: "",
      supplier_tax_status: "",
      supplier_transaction_type: "",
      supplier_NPWP: "",
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Request Method
  // =====================

  const getSupplierData = useCallback(async () => {
    try {
      setIsLoading(true);
      localStorage.setItem("searchValue", "");

      let url = Constant.BASE_API_URL + "/supplier?page=" + page;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      if (!data.suppliers) {
        setSupplierData(null);
      } else {
        setSupplierData(data);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setSupplierData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const createNewSupplier = async (supplierData) => {
    try {
      setIsLoading(true);
      const response = await fetch(Constant.BASE_API_URL + "/supplier", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          supplier_name: supplierData.supplier_name,
          supplier_address: supplierData.supplier_address,
          supplier_city: supplierData.supplier_city,
          supplier_phone_number: supplierData.supplier_phone_number,
          supplier_tax_status: supplierData.supplier_tax_status,
          supplier_transaction_type: supplierData.supplier_transaction_type,
          supplier_NPWP: supplierData.supplier_NPWP,
        }),
      });
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getSupplierData();
    }
  };

  const editSupplier = async (supplierData) => {
    try {
      setIsLoading(true);
      const supplierId = supplierData.supplier_id;
      const response = await fetch(
        Constant.BASE_API_URL + "/supplier/" + supplierId,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            supplier_name: supplierData.supplier_name,
            supplier_address: supplierData.supplier_address,
            supplier_city: supplierData.supplier_city,
            supplier_phone_number: supplierData.supplier_phone_number,
            supplier_tax_status: supplierData.supplier_tax_status,
            supplier_transaction_type: supplierData.supplier_transaction_type,
            supplier_NPWP: supplierData.supplier_NPWP,
          }),
        }
      );
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getSupplierData();
    }
  };

  const deleteSupplier = async () => {
    try {
      setIsLoading(true);
      const supplierId = idSupplierToDelete;
      const response = await fetch(
        Constant.BASE_API_URL + "/supplier/" + supplierId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdSupplierToDelete("");
      getSupplierData();
    }
  };

  const searchSupplier = (searchText) => {
    setSearchSupplierKeyword(searchText);
    setPage(1);
  };

  const getSupplierSearchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = new URL(Constant.BASE_API_URL + "/supplier/search");
      url.searchParams.append("keyword", searchSupplierKeyword);
      url.searchParams.append("page", page);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
      if (!data.suppliers) {
        setSupplierData(null);
      } else {
        setSupplierData(data);
      }
    } catch (error) {
      setSupplierData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchSupplierKeyword, navigate]);

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    if (searchSupplierKeyword !== "") {
      getSupplierSearchData();
    } else {
      getSupplierData();
    }
  }, [searchSupplierKeyword, getSupplierData, getSupplierSearchData]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Supplier</h1>
      <SupplierHeader
        onAddSupplierClicked={addNewSupplier}
        onSearchSupplier={searchSupplier}
      />

      <SupplierTable
        page={page}
        suppliers={supplierData.suppliers}
        onEditSupplierClicked={onEditSupplier}
        onDeleteSupplierClicked={onDeleteSupplier}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
      <ModalAddSupplier
        isShowModal={isOpenModalAddSupplier}
        onCloseModal={onCloseModal}
        onSubmitData={onSubmitNewSupplier}
        supplierObject={selectedSupplierData}
        key={Date.now()}
      />

      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeleteSupplier}
        onCloseModal={onCloseModalDeleteSupplier}
        onDeleteConfirmed={onDeleteSupplierConfirmed}
        idToDelete={idSupplierToDelete}
      />
    </div>
  );
};

export default SupplierList;
